import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ProductWrapper, ProductButton } from "./ProductStyled";
import useProductList from "./useProductList";
import { ProductItem } from "./ProductItem";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Products = () => {
  const navigate = useNavigate();
  const { list } = useProductList();
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const settings = {
    dots: false,
    slidesToShow: 4,
    infinite: list.length > 4,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "20px",
        },
      },
    ],
  };

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const slider = sliderRef.current;
    const button = buttonRef.current;

    if (!section || !header || !slider || !button) return;

    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        header.querySelectorAll(".sectionLabel, h2"),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Slider cards animation
      const cards = slider.querySelectorAll(".slick-slide");
      gsap.fromTo(
        cards,
        { opacity: 0, y: 60, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: slider,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Button animation
      gsap.fromTo(
        button,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: button,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, [list]);

  const onBtnClick = () => {
    navigate("/products");
  };

  return (
    <ProductWrapper ref={sectionRef}>
      <div className="productContainer">
        <div className="sectionHeader" ref={headerRef}>
          <div className="headerLeft">
            <div className="sectionLabel">
              <span className="line" />
              <span>Featured</span>
            </div>
            <h2>
              Our <span className="gradient">Products</span>
            </h2>
          </div>
        </div>

        <div ref={sliderRef}>
          <Slider {...settings}>
            {list?.map((item, index) => {
              return <ProductItem products={item} key={index} />;
            })}
          </Slider>
        </div>

        <div className="buttonWrapper" ref={buttonRef}>
          <ProductButton onClick={() => onBtnClick()}>
            View All Products
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </ProductButton>
        </div>
      </div>
    </ProductWrapper>
  );
};

export default Products;
